import { SyntheticEvent } from 'react'
import {
    DEFAULT_CRITERIA_PARAMETER_NAME,
    parseSearchCriteria,
    SearchCriteria,
    SearchCriteriaFilterActions,
    serializeSearchCriteria
} from '@corratech/pylot-filters-and-sort/src/hooks/use-plp-manager'
import Image from '@corratech/pylot-image'
import { ConfigurableProductOptionsValues } from '@pylot-data/pylotschema'
import { Button } from '@corratech/pylot-ui'
import cn from 'classnames'
import Link from 'next/link'
import router, { useRouter } from 'next/router'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import s from './ProductTile.module.scss'
import { MagePlazaProductBadge } from '@corratech/pylot-mageplaza-badges'
import { getPDPUrl } from '@lib/getPDPUrl'
import { useAddToCart } from '@components/alfakher/pylot-cart-manager'
import { CartData } from '@corratech/pylot-cart-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next'
import { AlfaRichContent as RichContent } from '@components/alfakher/pylot-rich-content'

import { getStoreConfig } from 'config'
import { YotpoStars } from '@components/YotpoStars/YotpoStars'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import {
    buildAddToCartItemFromProductVariant,
    trackAddedToCartFromItems
} from '@components/Klaviyo/src/KlaviyoAddToCartTracker'
import { useUser } from '@corratech/pylot-auth-manager'

export interface ProductTileProps {
    product: any
    imageParam: {
        width?: number
    }
    className?: string
    isPDP?: boolean
}
export interface Attribute {
    __typename: string
    code: string
    value_index: number
    label: string
}
export interface SmallImage {
    __typename: string
    url: string
}
export interface VariantProduct {
    __typename: string
    id: number
    small_image: SmallImage
    sku: string
    stock_status: string
}
export interface Variant {
    __typename: string
    attributes: Attribute[]
    product: VariantProduct
}
export const LoaderTile = () => (
    <div className={cn(s['productitem-block'])}>
        <Skeleton height={320} width={320} className={s['product-img']} />
        <Skeleton count={2} />
        <Skeleton circle width={16} count={2} />
    </div>
)

type CartItem = {
    sku: string
    quantity: number
}

export const saveItemToLocalCart = (
    item: CartItem,
    storageKey = 'addedToCartItems'
) => {
    if (typeof window === 'undefined') return

    const existingItems: CartItem[] = JSON.parse(
        localStorage.getItem(storageKey) || '[]'
    )

    const existingIndex = existingItems.findIndex((i) => i.sku === item.sku)

    if (existingIndex !== -1) {
        existingItems[existingIndex].quantity += item.quantity
    } else {
        existingItems.push(item)
    }

    localStorage.setItem(storageKey, JSON.stringify(existingItems))
}

export const FwrdProductTile = ({
    product = {},
    imageParam = {},
    className,
    isPDP = false
}: ProductTileProps): ReactElement | null => {
    const { addToCart, isAdding } = useAddToCart()
    const { openToast } = useUI()
    const { t } = useTranslation(['common', 'plp'])
    const { customer } = useUser()
    const { url_key, variant, quantity } = product
    const isConfig = product.__typename === 'ConfigurableProduct'
    const isSimple = product.__typename === 'SimpleProduct'
    const { query, locale } = useRouter()
    const search =
        typeof window !== 'undefined' && !isPDP
            ? new URL(window.location.href).search
            : ''
    const [urlSearchCriteria, setUrlSearchCriteria] = useState(search)
    const productUrl = `/${locale}${getPDPUrl(
        url_key,
        isConfig ? urlSearchCriteria : ''
    )}`
    const [selectedOption, setSelectedOption] = useState<SearchCriteria | null>(
        null
    )
    // @ts-ignore
    const { setGTMLocation } = useAlfakherContext()
    // @ts-ignore
    const { addToCartPopupPayload } = useAlfakherContext()

    // TODO: make dynamic?
    const swatchAttr = 'color'
    const colorsImagesMap = new Map()
    const fullImageUrl = useCallback(
        (imageUrl?: string): string => {
            const widthString = imageParam.width
                ? `&width=${imageParam.width}`
                : ''
            return `${imageUrl}?auto=webp&format=pjpg&crop=1:1${widthString}`
        },
        [imageParam.width]
    )
    const [image, setImage] = useState(fullImageUrl(product.small_image?.url))
    // During the changes in URL - convert new filters to product url
    useEffect(() => {
        if (!selectedOption) {
            setUrlSearchCriteria(search)
        }
        if (selectedOption) {
            onVariantChange({
                label: selectedOption.attribute_code,
                value_index: +selectedOption.filter_value
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        query,
        search,
        selectedOption?.attribute_code,
        selectedOption?.filter_value,
        urlSearchCriteria
    ])
    useEffect(() => {
        if (product.small_image?.url) {
            setImage(fullImageUrl(product.small_image?.url))
        }
    }, [fullImageUrl, product.small_image?.url])

    if (isConfig && product.variants) {
        product.variants.map((variant: Variant) => {
            const colorVariant = variant.attributes.find(
                (variant: { code: string }) => variant?.code === swatchAttr
            )
            const img = variant.product.small_image
                ? variant.product.small_image.url
                : false
            if (colorVariant && img) {
                colorsImagesMap.set(colorVariant.label, fullImageUrl(img))
            }
        })
    }
    const onVariantChange = ({
        label,
        value_index
    }: Partial<ConfigurableProductOptionsValues>) => {
        const value = value_index!.toString()
        const img = colorsImagesMap.get(label)
        if (img) {
            // setSelectedLabel(label!)
            setImage(img)
        }
        const searchCriteria = isPDP
            ? []
            : parseSearchCriteria(
                  query[DEFAULT_CRITERIA_PARAMETER_NAME] as string
              )
        const idxOfChangedAttribute = searchCriteria.findIndex(
            (sAttr) => sAttr.attribute_code === swatchAttr
        )
        let newOption: SearchCriteria
        if (idxOfChangedAttribute !== -1) {
            searchCriteria[idxOfChangedAttribute].filter_value = value
            newOption = searchCriteria[idxOfChangedAttribute]
        } else {
            newOption = {
                attribute_code: swatchAttr,
                filter_action: SearchCriteriaFilterActions.EQ,
                filter_value: value
            }
            searchCriteria.push(newOption)
        }
        const newSearchCriteriaString = `?${DEFAULT_CRITERIA_PARAMETER_NAME}=${serializeSearchCriteria(
            searchCriteria
        )}`
        setUrlSearchCriteria(newSearchCriteriaString)
        setSelectedOption(newOption)
    }

    const showAddToCartErrorMessage = (response: CartData | undefined) => {
        const errorMessages = response?.errors ?? response?.user_errors ?? null

        const errorCount = errorMessages?.length
        if (!errorCount) {
            const item = buildAddToCartItemFromProductVariant({
                product,
                variant,
                quantity,
                url: `${config.base.url.baseUrl}p/${product?.url_key}`
            })

            if (customer?.email) {
                trackAddedToCartFromItems([item], { email: customer.email })
            }
        }

        const allErrors = errorMessages
            ? errorMessages.map((errorMessage, i) => (
                  <>
                      <li key={i}>{errorMessage?.message}</li>
                      {errorCount !== i + 1 && <br />}
                  </>
              ))
            : ''
        if (allErrors.length) {
            openToast(<ul>{allErrors}</ul>, ToastType.Warning, true)
        }
    }

    if (!product) return null

    const handleAddToCart = async () => {
        if (product.addtocart_popup_cms && addToCartPopupPayload.isShowPopup) {
            setGTMLocation('UPSELL_PROMOTION')
            const cartItem = {
                sku: product.sku,
                quantity: 1
            }

            saveItemToLocalCart(cartItem)
        }
        if (isSimple && !isConfig) {
            const response = await addToCart(
                [
                    {
                        sku: product.sku,
                        selected_options: [],
                        quantity: 1
                    }
                ],
                [product]
            )
            showAddToCartErrorMessage(response)
        } else if (product.is_custom_kit) {
            router.push({
                pathname: productUrl,
                query: { ...router.query, option: 'customize' }
            })
        } else {
            router.push({
                pathname: productUrl,
                query: { ...router.query }
            })
        }
    }

    const out_of_stock = product.stock_status == 'OUT_OF_STOCK'

    let buttonLabel
    if (out_of_stock) {
        buttonLabel = t('SOLD OUT')
    } else {
        if (isAdding) {
            buttonLabel = t('Adding...')
        } else if (isSimple) {
            buttonLabel = t('Add to Bag')
        } else {
            buttonLabel = t('Customize')
        }
    }

    const handleProductTileClickd = (e: SyntheticEvent<EventTarget>) => {
        const target = e.target as Element
        const promotionalElement = target.closest('.promotional')
        if (promotionalElement) {
            const res = promotionalElement.className.split(' ')
            const promotionalClassName = res[res.length - 1]
            localStorage.setItem('promotionBanner', promotionalClassName)
        } else {
            localStorage.removeItem('promotionBanner')
        }
    }
    const config = getStoreConfig()

    const getPoductShortDescription = () => {
        if (product?.custom_kit_short_description) {
            return product?.custom_kit_short_description
        } else {
            return <RichContent html={product.short_description?.html} />
        }
    }
    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            className={cn(
                s['productitem-block'],
                className,
                'productitem-block'
            )}
            onClick={(e) => handleProductTileClickd(e)}
        >
            <div className={cn(s['product-eyebrow'], 'badge-info')}>
                {product.product_eyebrow_badge_text && (
                    <span
                        className={cn(
                            'default-badge',
                            product?.product_eyebrow_badge_text &&
                                String(
                                    product?.product_eyebrow_badge_text
                                ).toLowerCase()
                        )}
                    >
                        {product.product_eyebrow_badge_text}
                    </span>
                )}
            </div>
            <div className={cn('productimage-block')}>
                <div
                    className={cn(
                        s['productimg-link'],
                        'product-card-image-swipe',
                        'slider-product-img'
                    )}
                    aria-label={product.name}
                >
                    <div className={s['badges-container']}>
                        {product.mp_label_data && (
                            <MagePlazaProductBadge
                                labelData={product.mp_label_data}
                                showAllCustom
                            />
                        )}
                    </div>
                    <Link href={productUrl}>
                        <Image
                            src={image}
                            alt={product.name ? product.name : 'placeholder'}
                            width={390}
                            height={390}
                            className={s['product-img']}
                        />
                    </Link>
                </div>
            </div>
            <Link href={productUrl}>
                <div
                    className={cn(
                        s['plp-prod-details'],
                        'sliderproduct-detail'
                    )}
                >
                    <strong
                        className={cn(s['product-brand'], 'product-brand-name')}
                    >
                        {product.brand_text !== 'false'
                            ? product.brand_text
                            : ''}
                    </strong>
                    <a
                        className={(s['product-link'], 'product-link')}
                        href={productUrl}
                    >
                        <h5
                            className={cn(
                                s['product-name'],
                                'product-name uppercase text-sm leading-extra-4.4 md:text-lg font-medium tracking-extra-base cursor-pointer md:leading-5'
                            )}
                            title={product.name}
                        >
                            {product.name}
                        </h5>
                    </a>
                    <p className={cn(s['product-subtitle'], 'subtitle')}>
                        {product?.subtitle}
                    </p>
                    <p className={cn(s['short_description'], 'shortDesc')}>
                        {getPoductShortDescription()}
                    </p>
                    <div className="rating">
                        <YotpoStars
                            productId={product?.id}
                            yotoApiKey={config.base.yotpo.api_key}
                        />
                    </div>
                </div>
            </Link>
            <div className={cn('product-info')}>
                <div className={(s['product-details'], 'text-center')}>
                    <ProductPrice
                        className={String(product.__typename).toLowerCase()}
                        priceRange={product.price_range}
                        productType={product.__typename}
                        showDiscountPrice={!product?.is_bulk_product}
                    />
                </div>
            </div>
            <div className={s['plp-add-to-cart']}>
                <Button
                    aria-label={s['Add to Cart']}
                    type="button"
                    className="add-to-cart-button"
                    loading={false}
                    disabled={out_of_stock}
                    onClick={() => handleAddToCart()}
                >
                    {buttonLabel}
                </Button>
            </div>
        </div>
    )
}
export const ProductItem = React.memo(
    (props: ProductTileProps) => <FwrdProductTile {...props} />,
    (prevProps, newProps) =>
        JSON.stringify(prevProps) === JSON.stringify(newProps)
)
ProductItem.displayName = 'MemoProductItem'
